import { Injectable, OnDestroy } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import { AlertButtonColor, AlertButtonStyle } from "@obg/mat.ui.core";
import {
	AbstractTranslatableAlertService,
	AuthSelector,
	CasinoBlockSelector,
	CoreState,
	JurisdictionPrefixPipe,
	ProductType,
	ResponsibleGamingConfigService,
} from "@obg/ngx.core";
import { TranslationSelector } from "@obg/ngx.core.content";
import { RouteAction } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, Observable, Subscription } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

import { CasinoBlockService as AbstractCasinoBlockService } from "./casino-block.service.abstract";

const GAME_BLOCK_DIALOG_ID = "game-block";

@Injectable({
	providedIn: "root",
})
export class CasinoBlockService implements AbstractCasinoBlockService, OnDestroy {
	private data$$: Subscription | undefined;

	constructor(
		private translatableAlertService: AbstractTranslatableAlertService,
		private casinoBlockSelector: CasinoBlockSelector,
		private store: Store<CoreState>,
		private jurisdictionPrefixPipe: JurisdictionPrefixPipe,
		private config: ResponsibleGamingConfigService,
		private translationSelector: TranslationSelector,
		private customerSupportService: CustomerSupportService
	) {}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	checkCasinoBlocked$(): Observable<boolean> {
		return combineLatest([
			this.store.select(AuthSelector.isAuthenticated),
			this.store.pipe(select(this.casinoBlockSelector.isCasinoBlocked()), ignoreNil()),
		]).pipe(
			map(
				([isAuthenticated, isCasinoBlocked]) =>
					isAuthenticated && !!isCasinoBlocked && this.config.getCasinoBlockEnabled()
			)
		);
	}

	openGameBlockDialog(): void {
		this.data$$ = this.store
			.pipe(
				select(this.translationSelector.isBusy()),
				filter((isBusy) => !isBusy),
				take(1),
				tap(() =>
					this.translatableAlertService.open(
						{
							id: GAME_BLOCK_DIALOG_ID,
							testId: GAME_BLOCK_DIALOG_ID,
							title: this.jurisdictionPrefixPipe.transform("casino-block.game-block.title"),
							message: this.jurisdictionPrefixPipe.transform("casino-block.game-block.description"),
							dialogConfig: {
								closeOnNavigation: true,
								autoFocus: false,
							},
							buttons: [
								{
									style: AlertButtonStyle.Stroked,
									color: AlertButtonColor.Primary,
									text: "general.customer-support",
									handler: () => this.customerSupportService.openSupportPageInNewTab(),
								},
								{
									text: "general.sportsbook",
									handler: () => this.store.dispatch(RouteAction.navigate(ProductType.Sportsbook)),
								},
							],
						},
						[]
					)
				)
			)
			.subscribe();
	}
}
